/**
 * The shared behavior of a menu button and its menu: open and close, the
 * keys from `menuKeyboard.ts`, and closing on a press outside or on focus
 * that leaves. The "To app" menu and the "Open app" group both use it, so
 * the two menus act alike.
 */
import { type MenuAction, resolveButtonKey, resolveItemKey } from './menuKeyboard'

/** The parts of one menu that the behavior wires together. */
export interface MenuParts {
  /** The element that holds the button and the menu. A press inside it never closes the menu. */
  readonly root: HTMLElement
  /** The button that opens the menu. It gets `aria-expanded`. */
  readonly button: HTMLElement
  readonly menu: HTMLElement
  /** The menu items, in order. */
  readonly items: readonly HTMLElement[]
}

/** What a caller may do with a wired menu. */
export interface WiredMenu {
  readonly setOpen: (open: boolean) => void
}

/** Wires the click, keyboard, and outside-press behavior. Returns a handle to open or close the menu. */
export const wireMenu = ({ root, button, menu, items }: MenuParts): WiredMenu => {
  const closeOnOutside = (event: Event): void => {
    if (event.target instanceof Node && root.contains(event.target)) {
      return
    }
    setOpen(false)
  }

  function setOpen(open: boolean): void {
    menu.hidden = !open
    button.setAttribute('aria-expanded', String(open))
    const toggleListeners = open ? 'addEventListener' : 'removeEventListener'
    document[toggleListeners]('pointerdown', closeOnOutside)
    document[toggleListeners]('focusin', closeOnOutside)
  }

  const perform = (action: MenuAction): boolean => {
    if (action.kind === 'ignore') {
      return false
    }
    if (action.kind === 'close') {
      setOpen(false)
      if (action.returnFocus) {
        button.focus()
      }
      return true
    }
    setOpen(true)
    items[action.index]?.focus()
    return true
  }

  button.addEventListener('click', () => setOpen(menu.hidden))
  button.addEventListener('keydown', (event) => {
    if (perform(resolveButtonKey(event.key, items.length))) {
      event.preventDefault()
    }
  })
  menu.addEventListener('keydown', (event) => {
    const focused = items.findIndex((item) => item === document.activeElement)
    const action = resolveItemKey(event.key, focused, items.length)
    // Tab must keep its default move, so focus leaves the menu in order.
    if (perform(action) && event.key !== 'Tab') {
      event.preventDefault()
    }
  })
  for (const item of items) {
    item.addEventListener('click', () => setOpen(false))
  }

  return { setOpen }
}
